import styled from '@emotion/styled';
import Heading from 'storybookConfig/stories/molecules/Heading';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'storybookConfig/stories/molecules/Icon';
import Body from 'storybookConfig/stories/molecules/Body';
import TertiaryButton from 'storybookConfig/stories/molecules/Button/TertiaryButton';
import ZeroCostSvg from '../../images/zero_cost.svg';
import StandOutSvg from '../../images/stand_out.svg';
import GetInEarlySvg from '../../images/get_in_early.svg';
import CarroMDSvg from '../../images/carro_md.svg';
import TrustedSvg from '../../images/trusted.svg';

const PanelContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  gap: 12px;
`;

const StyledHeading = styled(Heading)`
  font-family: Montserrat;
  color: white;
  font-size: 48px;
  font-style: normal;
  font-weight: 600;
  line-height: 64px; /* 133.333% */
  letter-spacing: 0.48px;
  @media (max-width: 1200px) {
    font-size: 48px;
    line-height: 48px;
  }
`;

const HighlightedText = styled.span`
  color: ${({ theme }) => theme.color.success400};
  font-weight: 600;
`;

const SubHeading = styled(Heading)`
  color: white;
  font-weight: 600;
  font-size: 18px;
  line-height: 1.2;
`;

const ContentSection = styled.div`
  display: flex;
  gap: 48px;
  margin-top: 24px;
`;

const FeatureCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 360px;
`;

const FeatureCard = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: ${({ isActive }) =>
    isActive ? 'radial-gradient(circle at center, #6659FF 0%, #228AFF 100%)' : 'transparent'};
  border: 1px solid
    ${({ theme, isActive }) => (isActive ? theme.color.success500 : 'rgba(255, 255, 255, 0.3)')};
  border-radius: 12px;
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.color.success500};
    background: ${({ isActive }) =>
      isActive
        ? 'radial-gradient(circle at center, #6659FF 0%, #228AFF 100%)'
        : 'rgba(255, 255, 255, 0.05)'};
  }
`;

const FeatureIcon = styled.div<{ isActive: boolean }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, isActive }) => (isActive ? theme.color.success500 : theme.color.white)};
`;

const FeatureContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FeatureTitle = styled(Body)`
  color: white;
  font-weight: 600;
  font-size: 18px;
`;

const FeatureDescription = styled(Body)`
  color: white;
  font-size: 14px;
  line-height: 1.4;
  opacity: 0.8;
`;

const VisualizationArea = styled.div`
  flex: 1;
  min-height: 400px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;

  img {
    width: 100%;
    max-width: 600px;
    height: auto;
    object-fit: contain;
  }

  @media (max-width: 1385px) {
    display: none;
  }
`;

const BackLink = styled(TertiaryButton)`
  color: white;
  align-self: flex-start;
  margin-top: 48px;
`;

const BackLinkText = styled.span`
  text-decoration: underline;
`;

const features = [
  {
    id: 'zero-costs',
    icon: 'attach_money',
    title: 'Zero upfront costs',
    description: 'Leverage our vast network of retailers to increase sales',
  },
  {
    id: 'stand-out',
    icon: 'rocket',
    title: 'Stand Out Where It Matters',
    description: 'Match you with retailers who are aligned with your brand',
  },
  {
    id: 'early',
    icon: 'calendar_today',
    title: 'Get in early',
    description: 'Early suppliers get premium visibility and a head start',
  },
  {
    id: 'dropship',
    icon: 'swap_vert',
    title: 'Carro & Modern Dropship',
    description: 'Under one roof—more reach, more products, more growth.',
  },
  {
    id: 'trusted',
    icon: 'inventory_2',
    title: 'Trusted by Leading Brands',
    description: 'Your products, on the best shelves.',
  },
];

const ContactDescription = () => {
  const [activeFeature, setActiveFeature] = useState('zero-costs');
  const navigate = useNavigate();

  return (
    <PanelContainer>
      <StyledHeading variant="Headings/H1">
        Reach <HighlightedText>10,000</HighlightedText> new partners when Marketplace goes live
      </StyledHeading>
      <SubHeading variant="Headings/H2">
        Sell your products on thousands of top storefronts and marketplaces, exposing your brand to
        new customers without the cost of advertising.
      </SubHeading>
      <ContentSection>
        <FeatureCardsContainer>
          {features.map((feature) => (
            <FeatureCard
              key={feature.id}
              isActive={activeFeature === feature.id}
              onClick={() => setActiveFeature(feature.id)}
            >
              <FeatureIcon isActive={activeFeature === feature.id}>
                <Icon name={feature.icon} size="24px" />
              </FeatureIcon>
              <FeatureContent>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureContent>
            </FeatureCard>
          ))}
        </FeatureCardsContainer>
        <VisualizationArea>
          {(() => {
            switch (activeFeature) {
              case 'zero-costs':
                return <img src={ZeroCostSvg} alt="Zero upfront costs visualization" />;
              case 'stand-out':
                return <img src={StandOutSvg} alt="Stand out visualization" />;
              case 'early':
                return <img src={GetInEarlySvg} alt="Get in early visualization" />;
              case 'dropship':
                return <img src={CarroMDSvg} alt="Carro & Modern Dropship visualization" />;
              case 'trusted':
                return <img src={TrustedSvg} alt="Trusted by Leading Brands visualization" />;
              default:
                return <Body color="white">Visualization for {activeFeature}</Body>;
            }
          })()}
        </VisualizationArea>
      </ContentSection>
      <BackLink
        onClick={() => {
          navigate('/get-started');
        }}
        $iconName="arrow_back"
      >
        <BackLinkText>Not Now (Go Back To Modern Dropship)</BackLinkText>
      </BackLink>
    </PanelContainer>
  );
};

export default ContactDescription;
